import { type RenderCanvas, renderCanvas } from "./canvas-surface";
import { applyImageColorCorrection } from "./native-color-filter";
import { bilinear, inverseBilinear } from "../native/bilinear";
import { hasEdgeWarp, paintEdgeWarp } from "../geometry/edge-warp";
import type { SceneNode } from "../scene/model";
import type { Box } from "../native/geometry";
import { bounds } from "../native/geometry";
import { nodeMatrix } from "../native/transforms";
import { transformPoint } from "../geometry/affine";
import {
  inversePerspective,
  perspective,
  warpQuad,
} from "../native/deformation";
import { effects } from "../native/effects";
import {
  applyImageBackgroundRemovalToCanvas,
  applyImageEdgesToCanvas,
  getImageBlurPixels,
  boxBlurImageData,
  supportsReliableCanvasBlurFilters,
} from "./native-pixel-effects.js";
export function effectBounds(node: SceneNode): Box {
  const b = bounds(node),
    e = effects(node),
    blur = getImageBlurPixels(
      e.blur,
      node.geometry.width!,
      node.geometry.height!,
    ),
    pad = Math.ceil(
      blur * 3 +
        (e.shadow
          ? e.shadowStyle
            ? e.shadowStyle.blur * 3 +
              Math.max(Math.abs(e.shadowStyle.x), Math.abs(e.shadowStyle.y))
            : 45
          : 0) +
        3,
    );
  return {
    x: b.x - pad,
    y: b.y - pad,
    width: b.width + 2 * pad,
    height: b.height + 2 * pad,
  };
}
type Entry = { canvas: RenderCanvas; box: Box; density: number };
/** Derived display surfaces. Originals, vector outlines and effect settings remain in the scene. */
export class NativeEffectsRaster {
  private cache = new WeakMap<SceneNode, Entry>();
  get(
    node: SceneNode,
    density: number,
    paint: (
      flat: SceneNode,
      canvas: RenderCanvas,
      box: Box,
      scale: number,
    ) => void,
  ): Entry {
    const bucket = Math.pow(2, Math.ceil(Math.log2(Math.max(0.125, density)))),
      previous = this.cache.get(node);
    if (previous?.density === bucket) return previous;
    const e = effects(node),
      box = effectBounds(node),
      sourceSize = (node.geometry.warpSourceSize as
        { width: number; height: number } | undefined) ?? {
        width: node.geometry.width!,
        height: node.geometry.height!,
      };
    const scale = Math.min(
      bucket,
      8192 /
        Math.max(box.width, box.height, sourceSize.width, sourceSize.height),
      Math.sqrt(
        16000000 /
          Math.max(
            box.width * box.height,
            sourceSize.width * sourceSize.height,
          ),
      ),
    );
    const flat: SceneNode = {
      ...node,
      geometry: {
        ...node.geometry,
        width: sourceSize.width,
        height: sourceSize.height,
      },
      transform: { x: 0, y: 0, originX: "center", originY: "center" },
      effects: {},
      appearance: { ...node.appearance, opacity: 1, blendMode: "source-over" },
      layer: { ...node.layer, clipToBelow: false },
    };
    delete flat.geometry.warpCorners;
    delete flat.geometry.warpEdges;
    delete flat.geometry.warpSourceSize;
    delete flat.geometry.warpMapping;
    const sourceBox = bounds(flat),
      source = canvas(sourceBox.width * scale, sourceBox.height * scale);
    paint(flat, source, sourceBox, scale);
    source.getContext("2d")!.setTransform(1, 0, 0, 1, 0, 0);
    if (node.kind === "image") {
      applyImageBackgroundRemovalToCanvas(source, e.background);
      applyImageColorCorrection(source, e.hue, e.saturation);
      applyImageEdgesToCanvas(source, {
        ...e.edge,
        style: e.edge.style === "fade" ? "blur" : e.edge.style,
      });
    }
    const output = canvas(box.width * scale, box.height * scale),
      ctx = output.getContext("2d")!,
      m = nodeMatrix(node),
      q = warpQuad(node);
    const project = (u: number, v: number) => {
      const p = transformPoint(
        node.geometry.warpCorners
          ? perspective(q, u, v)
          : { x: u - 0.5, y: v - 0.5 },
        m,
      );
      return { x: (p.x - box.x) * scale, y: (p.y - box.y) * scale };
    };
    if (!node.geometry.warpCorners) {
      const a = project(
          sourceBox.x / sourceSize.width + 0.5,
          sourceBox.y / sourceSize.height + 0.5,
        ),
        b = project(
          (sourceBox.x + sourceBox.width) / sourceSize.width + 0.5,
          sourceBox.y / sourceSize.height + 0.5,
        ),
        c = project(
          sourceBox.x / sourceSize.width + 0.5,
          (sourceBox.y + sourceBox.height) / sourceSize.height + 0.5,
        );
      ctx.setTransform(
        (b.x - a.x) / source.width,
        (b.y - a.y) / source.width,
        (c.x - a.x) / source.height,
        (c.y - a.y) / source.height,
        a.x,
        a.y,
      );
      ctx.drawImage(source, 0, 0);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    } else {
      // Inverse projective resampling avoids translucent seams between mesh triangles.
      const quad = [project(0, 0), project(1, 0), project(1, 1), project(0, 1)];
      if (hasEdgeWarp(node.geometry.warpEdges)) {
        const offsets = node.geometry.warpEdges!.map((p) => ({
          x: (m[0] * p.x + m[2] * p.y) * scale,
          y: (m[1] * p.x + m[3] * p.y) * scale,
        }));
        const pixels = source
            .getContext("2d")!
            .getImageData(0, 0, source.width, source.height),
          target = ctx.createImageData(output.width, output.height);
        // The flat vector source includes its centered stroke outside [0,1].
        const margin = 2 * Math.max(0,
          -sourceBox.x / sourceSize.width - 0.5,
          -sourceBox.y / sourceSize.height - 0.5,
          (sourceBox.x + sourceBox.width) / sourceSize.width - 0.5,
          (sourceBox.y + sourceBox.height) / sourceSize.height - 0.5);
        paintEdgeWarp(pixels, target, quad, offsets, (local) => {
          const uv =
            node.geometry.warpMapping === "bilinear"
              ? local
              : inversePerspective(q, bilinear(q, local.x, local.y));
          return {
            x:
              ((uv.x - 0.5) * sourceSize.width - sourceBox.x) / sourceBox.width,
            y:
              ((uv.y - 0.5) * sourceSize.height - sourceBox.y) /
              sourceBox.height,
          };
        }, margin);
        ctx.putImageData(target, 0, 0);
      } else
        warpPixels(
          source,
          output,
          quad,
          sourceBox,
          sourceSize,
          node.geometry.warpMapping === "bilinear",
        );
    }
    const blur =
      getImageBlurPixels(e.blur, node.geometry.width!, node.geometry.height!) *
      scale;
    let filtered = output;
    if (blur > 0) {
      filtered = canvas(output.width, output.height);
      const c = filtered.getContext("2d")!;
      if (supportsReliableCanvasBlurFilters()) {
        c.filter = `blur(${blur}px)`;
        c.drawImage(output, 0, 0);
        c.filter = "none";
      } else
        c.putImageData(
          boxBlurImageData(
            ctx.getImageData(0, 0, output.width, output.height),
            Math.max(1, blur * 0.52),
          ),
          0,
          0,
        );
    }
    if (e.shadow) {
      const shadow = canvas(output.width, output.height),
        c = shadow.getContext("2d")!;
      const s = e.shadowStyle;
      c.shadowColor = s
        ? s.color +
          Math.round(s.alpha * 255)
            .toString(16)
            .padStart(2, "0")
        : "rgba(15,23,42,0.32)";
      c.shadowBlur = (s?.blur ?? 12) * scale;
      c.shadowOffsetX = (s?.x ?? 5) * scale;
      c.shadowOffsetY = (s?.y ?? 5) * scale;
      c.drawImage(filtered, 0, 0);
      filtered = shadow;
    }
    const entry = { canvas: filtered, box, density: bucket };
    this.cache.set(node, entry);
    return entry;
  }
}
function canvas(w: number, h: number) {
  const c = renderCanvas();
  c.width = Math.max(1, Math.ceil(w));
  c.height = Math.max(1, Math.ceil(h));
  return c;
}
function warpPixels(
  source: RenderCanvas,
  output: RenderCanvas,
  quad: { x: number; y: number }[],
  box: Box,
  size: { width: number; height: number },
  bilinear = false,
) {
  const [a, b, c, d] = quad as [
    { x: number; y: number },
    { x: number; y: number },
    { x: number; y: number },
    { x: number; y: number },
  ];
  const dx1 = b.x - c.x,
    dx2 = d.x - c.x,
    dx3 = a.x - b.x + c.x - d.x,
    dy1 = b.y - c.y,
    dy2 = d.y - c.y,
    dy3 = a.y - b.y + c.y - d.y,
    det = dx1 * dy2 - dx2 * dy1;
  const g = (dx3 * dy2 - dx2 * dy3) / det,
    h = (dx1 * dy3 - dx3 * dy1) / det,
    A = b.x - a.x + g * b.x,
    B = d.x - a.x + h * d.x,
    C = a.x,
    D = b.y - a.y + g * b.y,
    E = d.y - a.y + h * d.y,
    F = a.y;
  const inverse = [
    E - F * h,
    C * h - B,
    B * F - C * E,
    F * g - D,
    A - C * g,
    C * D - A * F,
    D * h - E * g,
    B * g - A * h,
    A * E - B * D,
  ];
  const unwarp = bilinear ? inverseBilinear(quad) : null;
  const sw = source.width,
    sh = source.height,
    pixels = source.getContext("2d")!.getImageData(0, 0, sw, sh).data,
    ctx = output.getContext("2d")!,
    data = ctx.createImageData(output.width, output.height),
    dest = data.data;
  for (let y = 0; y < output.height; y++)
    for (let x = 0; x < output.width; x++) {
      const X = x + 0.5,
        Y = y + 0.5,
        z = inverse[6]! * X + inverse[7]! * Y + inverse[8]!;
      if (Math.abs(z) < 1e-8) continue;
      const uv = unwarp?.(X, Y);
      const u = uv
          ? uv.x
          : (inverse[0]! * X + inverse[1]! * Y + inverse[2]!) / z,
        v = uv ? uv.y : (inverse[3]! * X + inverse[4]! * Y + inverse[5]!) / z;
      const sx = (((u - 0.5) * size.width - box.x) / box.width) * sw - 0.5,
        sy = (((v - 0.5) * size.height - box.y) / box.height) * sh - 0.5;
      if (sx < -0.5 || sy < -0.5 || sx > sw - 0.5 || sy > sh - 0.5) continue;
      const ix = Math.floor(sx),
        iy = Math.floor(sy),
        fx = sx - ix,
        fy = sy - iy,
        out = (y * output.width + x) * 4;
      let alpha = 0,
        r = 0,
        green = 0,
        blue = 0;
      for (let j = 0; j < 2; j++)
        for (let i = 0; i < 2; i++) {
          const xx = Math.max(0, Math.min(sw - 1, ix + i)),
            yy = Math.max(0, Math.min(sh - 1, iy + j)),
            index = (yy * sw + xx) * 4,
            weight = (i ? fx : 1 - fx) * (j ? fy : 1 - fy),
            aa = pixels[index + 3]! * weight;
          alpha += aa;
          r += pixels[index]! * aa;
          green += pixels[index + 1]! * aa;
          blue += pixels[index + 2]! * aa;
        }
      if (alpha) {
        dest[out] = r / alpha;
        dest[out + 1] = green / alpha;
        dest[out + 2] = blue / alpha;
        dest[out + 3] = alpha;
      }
    }
  ctx.putImageData(data, 0, 0);
}
